/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import moment from 'moment';
import 'moment/locale/pt-br';
import Cookies from 'js-cookie';
import ReactLoading from 'react-loading';
import api from 'api';
import { loadingColor, white } from 'styles/colorProvider';
import {
  PrintStyles,
  LoadingArea,
  ButtonArea,
  Sheet,
  DocumentHeader,
  DocumentSubtitle,
  Logo,
  IdentificationBand,
  Field,
  MonoValue,
  Badge,
  SectionTitle,
  SectionBody,
  Grid,
  Table,
  Totals,
  PaymentDetails,
  ServiceDescription,
  GuarantorBox,
  GuarantorNotice,
  Alert,
  Note,
  Footer,
  FooterLogo,
} from './styles';
import { formatCPF, formatCNPJ, formatPlate, formatPhone, formatCurrency } from 'react-data-formatter';
import { maskZipCode } from 'utils/masks';
import { Button } from 'components';
import { BsPrinter } from 'react-icons/bs';
import { cpf } from 'cpf-cnpj-validator';
import { formatPolicyStatus, PartnerProduct, GUARANTOR, SEVEN_BRAND, EMPTY_VALUE } from 'utils/general';
import { addMonthsByModality, FidelityDiscountLabelByModality, translatePolicyModality } from 'utils/policyModality';

function BackofficePrintPolicy({ location }) {
  const policyId = parseInt(location.pathname.split('detalhes-do-contrato/')[1]);
  useEffect(() => loadData(), []);
  const [policyData, setPolicyData] = useState({});
  const [loading, setLoading] = useState(false);
  const [valueService, setValueService] = useState(0);
  const [hasServices, setHasServices] = useState(false);
  const [hasCoverages, setHasCoverages] = useState(false);
  const shouldDisplayModalityDiscountObservation =
    policyData?.header?.modality_discount_type === 'fidelity' &&
    !!policyData?.header?.modality_discount &&
    policyData?.header?.modality !== 'monthly';

  const loadData = async () => {
    const policyIssue = {
      method: 'GET',
      url: `/get-issue-document/${policyId}`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: Cookies.get('splitrisk-authtoken'),
      },
      json: true,
    };
    setLoading(true);
    const response = await api(policyIssue);
    let valueService = 0;
    let hasCoverages = false;
    let hasServices = false;
    for (const item of response.data?.policyData?.coverages || []) {
      if (item.isService) {
        hasServices = true;
        valueService += Number(item.value);
      } else {
        hasCoverages = true;
      }
    }

    if (response?.data?.policyData?.oldCoverages?.length) {
      hasCoverages = true;
      if (response?.data?.proposalData) response.data.proposalData.iof = response?.data?.price?.iof;
    }

    setPolicyData(response.data);
    setHasCoverages(hasCoverages);
    setHasServices(hasServices);
    setLoading(false);
    setValueService(valueService);
  };

  // Manter essa alternância entre adjustment e adjustment_factor
  // PDF's antigos podem contar o outro parâmetro
  const adjustmentFactor = policyData?.vehicle?.auction
    ? '70%'
    : (policyData?.policyData?.adjustment_factor || policyData?.policyData?.adjustment) + '%';

  const isCpf = cpf.isValid(policyData?.client?.cpf);
  const isEnterprise = policyData?.findAgent?.product === PartnerProduct.enterprise;
  const isMonthly = policyData?.header?.modality === 'monthly';
  const coverages = policyData?.policyData?.coverages || [];
  const assistance = policyData?.policyData?.provider_assistance;
  const totalPremium = policyData?.isHinova ? policyData?.valueFinal : policyData?.price?.total_premium;
  const valueIof = policyData?.proposalData?.iof || 0;
  const valueNetCoverages = (totalPremium || 0) - valueService - valueIof;
  const valueLabel = isMonthly ? 'Valor mensal' : 'Valor';
  const totalLabel = isMonthly ? 'Valor total mensal' : 'Valor total';

  const handlePolicyValidityStartDate = () => {
    if (isEnterprise) {
      return moment(policyData?.header?.expiry_date).startOf('month').format('DD/MM/YYYY');
    }

    return isMonthly
      ? moment(policyData?.header?.expiry_date)
          .subtract(1, 'months')
          .set('date', moment(policyData?.header?.expiry_date).date())
          .format('DD/MM/YYYY')
      : moment(policyData?.policyData?.created_at).format('DD/MM/YYYY');
  };

  const handlePolicyValidityEndDate = () =>
    isMonthly
      ? moment.utc(policyData?.header?.expiry_date).format('DD/MM/YYYY [às] HH:mm')
      : addMonthsByModality(policyData?.policyData?.created_at, policyData?.header?.modality).format('DD/MM/YYYY [às] HH:mm');

  const dueDay = policyData?.policyData?.due_day || moment(policyData?.header?.expiry_date).format('DD');

  return (
    <>
      <PrintStyles />

      {loading && (
        <LoadingArea>
          <ReactLoading color={loadingColor} height={40} width={40} type="spin" />
        </LoadingArea>
      )}

      {!loading && (
        <>
          <ButtonArea>
            <Button style={{ color: white }} height="30px" width="130px" onClick={() => window.print()}>
              <BsPrinter size="15" style={{ marginRight: 10 }} />
              Imprimir
            </Button>
          </ButtonArea>

          <Sheet>
            <DocumentHeader>
              <div>
                <h1>
                  Contrato de Prestação de Serviços
                  <br />
                  Auto — Seven Seguros
                </h1>
                <DocumentSubtitle>
                  Documento com o resumo dos serviços contratados, valores e condições de utilização. Guarde-o para consulta
                  durante toda a vigência.
                </DocumentSubtitle>
              </div>
              {policyData?.logo && <Logo src={policyData.logo} alt={SEVEN_BRAND.name} />}
            </DocumentHeader>

            <IdentificationBand>
              <Field>
                <strong>Nº do contrato</strong>
                <MonoValue>{policyData?.header?.numberPolicy || EMPTY_VALUE}</MonoValue>
              </Field>
              <Field>
                <strong>Nº da proposta</strong>
                <MonoValue>{policyData?.header?.numberProposal || EMPTY_VALUE}</MonoValue>
              </Field>
              <Field>
                <strong>Situação</strong>
                <Badge>{formatPolicyStatus(policyData?.status)?.toUpperCase() || EMPTY_VALUE}</Badge>
              </Field>
              <Field>
                <strong>Modalidade</strong>
                <span>{translatePolicyModality(policyData?.header?.modality) || EMPTY_VALUE}</span>
              </Field>
              <Field>
                <strong>Início da vigência</strong>
                <span>{handlePolicyValidityStartDate()}</span>
              </Field>
              <Field>
                <strong>Fim da vigência</strong>
                <span>{handlePolicyValidityEndDate()}</span>
              </Field>
              <Field>
                <strong>Nº do bilhete</strong>
                <MonoValue>{policyData?.header?.numberPolicy || EMPTY_VALUE}</MonoValue>
              </Field>
              <Field>
                <strong>Emissão</strong>
                <span>{moment(policyData?.policyData?.created_at).format('DD/MM/YYYY')}</span>
              </Field>
            </IdentificationBand>

            {policyData?.stipulating && (
              <>
                <SectionTitle>Estipulante</SectionTitle>
                <SectionBody>
                  <Grid $cols={3}>
                    <Field>
                      <strong>Nome do estipulante</strong>
                      <span>{policyData?.stipulating?.name?.toUpperCase()}</span>
                    </Field>
                    <Field>
                      <strong>CNPJ</strong>
                      <MonoValue>{formatCNPJ(policyData?.stipulating?.document)}</MonoValue>
                    </Field>
                    <Field>
                      <strong>Endereço — CEP</strong>
                      <span>
                        {policyData?.stipulating?.address} — {policyData?.stipulating?.zipcode}
                      </span>
                    </Field>
                  </Grid>
                </SectionBody>
              </>
            )}

            <SectionTitle>Cliente</SectionTitle>
            <SectionBody>
              <Grid $cols={3}>
                <Field>
                  <strong>Nome / Razão social</strong>
                  <span>{policyData?.client?.name?.toUpperCase() || EMPTY_VALUE}</span>
                </Field>
                <Field>
                  <strong>CPF / CNPJ</strong>
                  <MonoValue>{isCpf ? formatCPF(policyData?.client?.cpf) : formatCNPJ(policyData?.client?.cpf)}</MonoValue>
                </Field>
                <Field>
                  <strong>Data de nascimento</strong>
                  <span>{moment(policyData?.client?.birthdate).utcOffset(0).format('DD/MM/YYYY')}</span>
                </Field>
                <Field>
                  <strong>E-mail</strong>
                  <span>{policyData?.client?.email || EMPTY_VALUE}</span>
                </Field>
                <Field>
                  <strong>Telefone</strong>
                  <MonoValue>{formatPhone(policyData?.client?.phone)}</MonoValue>
                </Field>
                <Field>
                  <strong>Sexo</strong>
                  <span>{policyData?.client?.gender === 1 ? 'Masculino' : 'Feminino'}</span>
                </Field>
                <Field>
                  <strong>Endereço</strong>
                  <span>
                    {policyData?.address?.street}, {policyData?.address?.number}
                  </span>
                </Field>
                <Field>
                  <strong>Bairro</strong>
                  <span>{policyData?.address?.district || EMPTY_VALUE}</span>
                </Field>
                <Field>
                  <strong>Cidade / UF — CEP</strong>
                  <span>
                    {policyData?.address?.city} / {policyData?.address?.state} — {maskZipCode(policyData?.address?.zip_code)}
                  </span>
                </Field>
              </Grid>
            </SectionBody>

            <SectionTitle>Condutor principal</SectionTitle>
            <SectionBody>
              <Grid $cols={3}>
                <Field>
                  <strong>Nome do condutor</strong>
                  <span>{policyData?.proposalData?.maindriver_name || policyData?.proposalData?.user?.name || EMPTY_VALUE}</span>
                </Field>
                <Field>
                  <strong>CPF</strong>
                  <MonoValue>
                    {formatCPF(policyData?.proposalData?.maindriver_document || policyData?.proposalData?.user?.cpf)}
                  </MonoValue>
                </Field>
                {policyData?.proposalData?.maindriver_birthdate && (
                  <Field>
                    <strong>Data de nascimento</strong>
                    <span>{moment(policyData?.proposalData?.maindriver_birthdate).utcOffset(0).format('DD/MM/YYYY')}</span>
                  </Field>
                )}
              </Grid>
            </SectionBody>

            <SectionTitle>Veículo</SectionTitle>
            <SectionBody>
              <Grid $cols={4}>
                <Field>
                  <strong>Fabricante</strong>
                  <span>{policyData?.vehicle?.TB_CONSTANT_VEHICLE_BRANDS?.name || EMPTY_VALUE}</span>
                </Field>
                <Field>
                  <strong>Modelo</strong>
                  <span>{policyData?.vehicle?.TB_CONSTANT_VEHICLE_MODELS?.name || EMPTY_VALUE}</span>
                </Field>
                <Field>
                  <strong>Ano fab. / modelo</strong>
                  <span>
                    {policyData?.vehicle?.manufacture_year} / {policyData?.vehicle?.year_model}
                  </span>
                </Field>
                <Field>
                  <strong>Cor</strong>
                  <span>{policyData?.vehicle?.TB_CONSTANT_VEHICLE_COLOR?.name || EMPTY_VALUE}</span>
                </Field>
                <Field>
                  <strong>Placa</strong>
                  <MonoValue>{policyData?.vehicle?.plate ? formatPlate(policyData?.vehicle?.plate) : 'Sem placa'}</MonoValue>
                </Field>
                <Field>
                  <strong>Chassi</strong>
                  <MonoValue>{policyData?.vehicle?.chassi || EMPTY_VALUE}</MonoValue>
                </Field>
                <Field>
                  <strong>Código FIPE</strong>
                  <MonoValue>{policyData?.vehicle?.TB_CONSTANT_VEHICLE_MODELS?.fipe_code || EMPTY_VALUE}</MonoValue>
                </Field>
                <Field>
                  <strong>Valor FIPE na contratação</strong>
                  <MonoValue>{policyData?.vehicle?.value ? formatCurrency(policyData?.vehicle?.value) : EMPTY_VALUE}</MonoValue>
                </Field>
                <Field>
                  <strong>Uso do veículo</strong>
                  <span>{policyData?.vehicle?.TB_CONSTANT_VEHICLE_USES?.name || EMPTY_VALUE}</span>
                </Field>
                <Field>
                  <strong>Região de circulação</strong>
                  <span>
                    {policyData?.address?.city} / {policyData?.address?.state}
                  </span>
                </Field>
                <Field>
                  <strong>Possui GNV</strong>
                  <span>{policyData?.vehicle?.gnv ? 'Sim' : 'Não'}</span>
                </Field>
                <Field>
                  <strong>Veículo 0 km</strong>
                  <span>{policyData?.vehicle?.zero_km ? 'Sim' : 'Não'}</span>
                </Field>
                <Field>
                  <strong>Chassi remarcado / recuperado de sinistro</strong>
                  <span>{policyData?.vehicle?.remarked ? 'Sim' : 'Não'}</span>
                </Field>
                <Field>
                  <strong>Veículo de leilão</strong>
                  <span>{policyData?.vehicle?.auction ? 'Sim' : 'Não'}</span>
                </Field>
              </Grid>
            </SectionBody>

            <SectionTitle>Condições do contrato</SectionTitle>
            <SectionBody>
              <Grid $cols={3}>
                <Field>
                  <strong>Modalidade de contratação</strong>
                  <span>VMR — Valor de Mercado Referenciado</span>
                </Field>
                <Field>
                  <strong>Fator de ajuste LMI</strong>
                  <span>{adjustmentFactor}</span>
                </Field>
                <Field>
                  <strong>Peças e reparos</strong>
                  <span>Novas, usadas, originais ou não</span>
                </Field>
              </Grid>
              <Note>
                <b>Limite Máximo de Indenização (LMI):</b> valor máximo a ser pago para cada serviço contratado, escolhido pelo
                CONTRATANTE e expresso neste contrato.
                <br />
                <b>Fator de ajuste:</b> percentual definido no ato da contratação, aplicado sobre o LMI para estipular o valor da
                indenização integral do veículo. Sua aplicação pode resultar em valor inferior ao LMI informado na proposta,
                conforme as características do veículo e seu estado de conservação. Caso seja selecionada a opção de leilão, o
                fator de ajuste será de 70%.
                <br />
                <b>Peças e reparos:</b> utilização de peças novas, usadas, originais ou não, desde que mantenham a especificação
                técnica do fabricante.
              </Note>
              {shouldDisplayModalityDiscountObservation && (
                <Note>
                  <b>Desconto de fidelidade:</b> será aplicado {policyData?.header?.modality_discount}% de desconto na{' '}
                  {FidelityDiscountLabelByModality[policyData?.header?.modality]} parcela do contrato.
                </Note>
              )}
            </SectionBody>

            {hasCoverages && (
              <>
                <SectionTitle $variant="yellow">Serviços contratados</SectionTitle>
                <SectionBody>
                  <Table>
                    <thead>
                      <tr>
                        <th style={{ width: '40%' }}>Serviço</th>
                        <th className="num">LMI</th>
                        <th style={{ width: '22%' }}>Participação obrigatória</th>
                        <th className="num">{valueLabel}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coverages
                        .filter((item) => item.isService === false)
                        .map((product) => (
                          <tr key={product?.name}>
                            <td className="service">{product?.name}</td>
                            <td className="num">{product?.lmi ? formatCurrency(product?.lmi) : EMPTY_VALUE}</td>
                            <td>{product?.deductible || <span className="empty">Não há</span>}</td>
                            <td className="num">{formatCurrency(product?.value || 0)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                  <Note>
                    Em caso de indenização integral, roubo ou furto total, será aplicada a coparticipação regressiva estabelecida
                    nas condições gerais deste contrato.
                  </Note>
                </SectionBody>
              </>
            )}

            {hasServices && (
              <>
                <SectionTitle $variant="yellow">Serviços adicionais</SectionTitle>
                <SectionBody>
                  <Table>
                    <thead>
                      <tr>
                        <th style={{ width: '40%' }}>Serviço</th>
                        <th className="num">LMI</th>
                        <th style={{ width: '22%' }}>Participação obrigatória</th>
                        <th className="num">{valueLabel}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {coverages
                        .filter((item) => item.isService === true)
                        .map((product) => (
                          <tr key={product?.name}>
                            <td className="service">{product?.name}</td>
                            <td className="num">{product?.lmi ? formatCurrency(product?.lmi) : EMPTY_VALUE}</td>
                            <td>{product?.deductible || <span className="empty">Não há</span>}</td>
                            <td className="num">{formatCurrency(product?.value || 0)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </SectionBody>
              </>
            )}

            <SectionTitle>{valueLabel} do contrato</SectionTitle>
            <SectionBody>
              <Totals>
                <div>
                  <strong>Valor líquido dos serviços</strong>
                  <span>{formatCurrency(valueNetCoverages)}</span>
                </div>
                <div>
                  <strong>Custo de serviços</strong>
                  <span>{formatCurrency(valueService)}</span>
                </div>
                <div>
                  <strong>IOF</strong>
                  <span>{formatCurrency(valueIof)}</span>
                </div>
                <div className="highlight">
                  <strong>{totalLabel}</strong>
                  <span>{formatCurrency(totalPremium || 0)}</span>
                </div>
              </Totals>
              <PaymentDetails>
                <Field>
                  <strong>Forma de pagamento</strong>
                  <span>{policyData?.price?.payment_method || EMPTY_VALUE}</span>
                </Field>
                <Field>
                  <strong>Dia de vencimento</strong>
                  <MonoValue>{dueDay}</MonoValue>
                </Field>
                <Field>
                  <strong>Recorrência</strong>
                  <span>{translatePolicyModality(policyData?.header?.modality) || EMPTY_VALUE}</span>
                </Field>
              </PaymentDetails>
            </SectionBody>

            {!!coverages.length && (
              <>
                <SectionTitle>Descrição dos serviços contratados</SectionTitle>
                <SectionBody>
                  <ServiceDescription>
                    {coverages.map((product) => (
                      <React.Fragment key={product?.name}>
                        <h3>{product?.name}</h3>
                        <p>{product?.description}</p>
                      </React.Fragment>
                    ))}
                  </ServiceDescription>
                </SectionBody>
              </>
            )}

            <SectionTitle $variant="cyan">Garantidora do risco</SectionTitle>
            <GuarantorBox>
              <Grid $cols={3}>
                <Field>
                  <strong>Razão social</strong>
                  <span>{GUARANTOR.socialReason}</span>
                </Field>
                <Field>
                  <strong>CNPJ</strong>
                  <MonoValue>{GUARANTOR.cnpj}</MonoValue>
                </Field>
                <Field>
                  <strong>Processo SUSEP</strong>
                  <MonoValue>{GUARANTOR.susepProcess}</MonoValue>
                </Field>
                <Field>
                  <strong>SAC da garantidora</strong>
                  <MonoValue>{GUARANTOR.sac}</MonoValue>
                </Field>
                <Field>
                  <strong>Ouvidoria</strong>
                  <MonoValue>{GUARANTOR.ombudsman}</MonoValue>
                </Field>
                <Field>
                  <strong>Condições gerais</strong>
                  <span>{GUARANTOR.generalConditions}</span>
                </Field>
              </Grid>
              <GuarantorNotice>
                Esse plano de seguro está registrado junto à SUSEP através do nº de processo {GUARANTOR.susepProcess}. As
                condições contratuais dos planos de seguro poderão ser consultadas no sítio eletrônico www.susep.gov.br, a partir
                desse mesmo número.
              </GuarantorNotice>
            </GuarantorBox>

            <SectionTitle>SUSEP</SectionTitle>
            <SectionBody>
              <Note style={{ marginTop: 0 }}>
                A SUSEP — Superintendência de Seguros Privados — é a autarquia federal responsável pela fiscalização, normatização
                e controle dos mercados de seguro, previdência complementar aberta, capitalização, resseguro e corretagem de
                seguros. As condições contratuais deste produto, protocolizadas junto à SUSEP, podem ser consultadas em
                www.susep.gov.br, de acordo com o número do processo constante neste contrato/proposta. O registro do produto é
                automático e não representa aprovação ou recomendação por parte da SUSEP. Telefone de atendimento exclusivo ao
                consumidor na SUSEP (9h30 às 17h): 0800 021 8484. O CONTRATANTE também poderá utilizar a plataforma oficial de
                registro de reclamações do consumidor em www.consumidor.gov.br.
              </Note>
            </SectionBody>

            <SectionTitle>Canais de atendimento {SEVEN_BRAND.name}</SectionTitle>
            <SectionBody>
              <Grid $cols={3}>
                <Field>
                  <strong>SAC</strong>
                  <MonoValue>{assistance?.phone_sac || EMPTY_VALUE}</MonoValue>
                </Field>
                <Field>
                  <strong>Assistência 24h</strong>
                  <MonoValue>{assistance?.phone_support || EMPTY_VALUE}</MonoValue>
                </Field>
                <Field>
                  <strong>E-mail</strong>
                  <span>{assistance?.email || EMPTY_VALUE}</span>
                </Field>
              </Grid>
            </SectionBody>

            <Alert>
              <b>Leia com atenção.</b> É fundamental que o CONTRATANTE conheça as cláusulas de <b>riscos excluídos</b>,{' '}
              <b>perdas de direitos</b>, <b>obrigações do contratante</b> e{' '}
              <b>participação proporcional ou participação obrigatória</b>, constantes nas condições gerais.
              <br />
              <br />
              Sobre os valores dos serviços incidem os tributos previstos na legislação vigente. O valor deste contrato foi
              calculado com base nas informações declaradas na proposta — sua veracidade e correção são imprescindíveis para a
              garantia dos serviços contratados. Confira todos os dados deste documento e, havendo necessidade de alteração ou
              retificação, comunique imediatamente a {SEVEN_BRAND.name}. Toda e qualquer alteração posterior também deve ser
              comunicada, podendo gerar cobrança complementar ou restituição de valores, conforme o caso.
              <br />
              <br />A {SEVEN_BRAND.name} está aderente à <b>Lei nº 13.709/2018 (LGPD)</b>, garantindo tratamento adequado aos
              dados pessoais do CONTRATANTE conforme a Política de Privacidade disponível em {SEVEN_BRAND.site}.
            </Alert>

            <Footer>
              <p>
                Contrato emitido por <b>{assistance?.social_reason}</b> — CNPJ {assistance?.cnpj}. Risco garantido pela{' '}
                <b>{GUARANTOR.socialReason}</b> — CNPJ {GUARANTOR.cnpj}.
              </p>
              {policyData?.logo && <FooterLogo src={policyData.logo} alt={SEVEN_BRAND.name} />}
            </Footer>
          </Sheet>
        </>
      )}
    </>
  );
}

export default BackofficePrintPolicy;
